/**
 * "Do we restore the last project, or show the picker?" — ONE decision, and it is
 * never allowed to guess.
 *
 * THIS IS THE RULE 5b0c1c2 PAID FOR. It used to be a flat `setTimeout(…, 1000)`,
 * and it lost the race on nearly every reload. Measured on the live app:
 *
 *     +187ms   GetProjectlist SENT
 *     +2101ms  timer fires, projectList is empty
 *     +2104ms  "No project selected, opening project modal"   <- decides
 *     +2142ms  list arrives, 72 projects                      <- 38 ms too late
 *
 * The saved project was in localStorage the entire time; the list was one HTTP
 * response away. The damage came from the else branch treating "the list has not
 * loaded yet" as "no project was chosen", so a reload threw the user back to
 * selection — and its own log line said "No project selected" when one was, which
 * is why it read as a routing bug for so long.
 *
 * So `wait` is a FIRST-CLASS OUTCOME here, exactly as it is in the project gate and
 * in the auth guard's MSAL settle. Every other branch is reachable only with a
 * KNOWN list state, which is what makes "show the picker" mean "there is genuinely
 * nothing to restore" rather than "I gave up waiting".
 *
 * Pure, so the rule can be asserted without a timer, a router or an HTTP round
 * trip — and so a mutation that reintroduces a timer, or that decides on an
 * unsettled list, fails a test instead of shipping.
 */

/** The three states the project list can be in, as the load stream reports them. */
export type ProjectListState = 'idle' | 'loading' | 'loaded' | 'failed';

export type RestoreDecision =
  /** A deep link outranks the persisted project; that path owns the selection. */
  | { kind: 'deep-link' }
  /** The list has not settled. DO NOT DECIDE. Not a denial — an absence of information. */
  | { kind: 'wait' }
  /** The saved project exists in the settled list. */
  | { kind: 'restore'; projectName: string }
  /** Nothing to restore, and we know it. `clearSaved` when the saved name is stale. */
  | { kind: 'show-picker'; reason: 'no-saved' | 'load-failed' | 'saved-project-gone'; clearSaved: boolean };

export interface RestoreInput {
  listState: ProjectListState;
  /** Persisted last selection, or null. */
  savedProjectName: string | null;
  /** Names in the served list. Only meaningful once `listState` has settled. */
  projectNames: readonly string[];
  /** Was a project named in the URL the bundle loaded with? */
  deepLinkPresent: boolean;
}

export function decideRestore(input: RestoreInput): RestoreDecision {
  // A DEEP LINK OUTRANKS THE PERSISTED PROJECT. Whoever followed the link asked
  // for a specific project; that is a stronger signal than what this browser
  // happened to look at yesterday. Checked first, and before the wait, because the
  // answer does not depend on the list at all.
  if (input.deepLinkPresent) return { kind: 'deep-link' };

  // THE WAIT. Anything below this line is reached with a known list state.
  if (input.listState !== 'loaded' && input.listState !== 'failed') {
    return { kind: 'wait' };
  }

  if (!input.savedProjectName) {
    return { kind: 'show-picker', reason: 'no-saved', clearSaved: false };
  }

  if (input.listState === 'failed') {
    // The list is the only way to know whether the saved project still exists.
    // Without it, restoring could select something deleted — so offer the picker,
    // but KEEP the saved name: the failure says nothing about whether it is valid.
    return { kind: 'show-picker', reason: 'load-failed', clearSaved: false };
  }

  if (input.projectNames.includes(input.savedProjectName)) {
    return { kind: 'restore', projectName: input.savedProjectName };
  }

  // Present, settled, and genuinely gone — the only branch where forgetting it is
  // right. Clearing on a FAILED load would lose a valid selection to an outage.
  return { kind: 'show-picker', reason: 'saved-project-gone', clearSaved: true };
}

/**
 * How long to wait before treating an unsettled list as failed.
 *
 * Bounded so a load that never settles cannot leave the user with neither content
 * nor a picker — the failure mode of waiting forever is worse than the failure mode
 * of deciding late.
 */
export const PROJECT_LIST_SETTLE_MS = 15000;
